import { Button, Card, Flex, Form } from 'antd'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  useQuestionnairesServiceAnswerQuestionnaireEndpoint,
  useQuestionnairesServiceGetQuestionnaireEndpoint
} from 'src/api/queries'
import { CustomItemRender, LoadingScreen, Upload } from 'src/components'
import { useStyles } from 'src/features/questionnaires/components/Preview/components/AnswerCard/styles'
import { useFileHandler } from 'src/hooks'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'

import { Preview } from '../components/Preview'

type AnswerData = {
  choices: string[]
  otherText?: string | null
  isOtherSelected?: boolean
}

type FormValues = {
  answers: Record<string, AnswerData>
}

const transformAnswersToQuestions = (formValues: FormValues) => {
  const answers = formValues?.answers || {}

  return Object.entries(answers).map(([id, answerData]) => {
    const {
      choices = [],
      otherText = null,
      isOtherSelected = false
    } = answerData || {}

    return {
      id,
      choices,
      isOtherSelected,
      ...(isOtherSelected ? { otherText } : {})
    }
  })
}

export const PatientQuestionnaireAnswer = () => {
  const { id } = useParams()
  const [isFormInitialized, setIsFormInitialized] = useState(false)

  const navigate = useNavigate()
  const { t } = useLocales()

  const { styles } = useStyles()
  const [form] = Form.useForm()

  const { data, isFetching } = useQuestionnairesServiceGetQuestionnaireEndpoint(
    {
      id: String(id)
    }
  )

  const { onFileChange, saveFiles } = useFileHandler({
    form,
    folder: `quiz-${id}`
  })

  useEffect(() => {
    if (data && Object.keys(data).length > 0) {
      form.setFieldsValue({
        roTitle: data?.name?.ro,
        ruTitle: data?.name?.ru,
        roDescription: data?.description?.ro,
        ruDescription: data?.description?.ru,
        questions: data?.questions?.map((question) => ({
          id: question?.id,
          text: {
            ro: question?.text?.ro,
            ru: question?.text?.ru
          },
          description: {
            ro: question?.description?.ro,
            ru: question?.description?.ru
          },
          type: question?.type,
          hasOther: question?.hasOther ?? false,
          order: question?.order,
          otherChoiceId: question?.otherChoiceId,
          answerChoices: question?.answerChoices?.map((answerChoice) => ({
            id: answerChoice?.id,
            text: {
              ro: answerChoice?.text?.ro,
              ru: answerChoice?.text?.ru
            },
            media: {
              mediaFile: {
                fileName: answerChoice?.mediaFile?.name,
                uid: answerChoice?.mediaFile?.id,
                id: answerChoice?.mediaFile?.id
              }
            },
            order: answerChoice?.order
          }))
        }))
      })
      setIsFormInitialized(true)
    }
  }, [data, form, isFormInitialized])

  const quizData = form?.getFieldsValue(true)

  const { mutate, isPending } =
    useQuestionnairesServiceAnswerQuestionnaireEndpoint({
      onSuccess: () => {
        navigate(paths.questionnaires.root)
      }
    })

  const onSubmit = async () => {
    const values = await form.validateFields()

    const updatedFiles = await saveFiles()

    mutate({
      id: String(id),
      requestBody: {
        questions: transformAnswersToQuestions(values),
        mediaItems: updatedFiles
      }
    })
  }

  if (isFetching || !isFormInitialized || !Object.values(quizData).length) {
    return <LoadingScreen />
  }

  return (
    <Form
      layout="vertical"
      form={form}
      onSubmitCapture={onSubmit}
      requiredMark={false}
      key={JSON.stringify(quizData)}
    >
      <Card className={styles.card}>
        <Flex
          vertical
          className={styles.questionWrapper}
          justify="center"
          align="start"
          gap={48}
        >
          <Preview form={form} />

          <Form.Item name="docs" className="full-width">
            <Upload
              onChange={onFileChange}
              customRequest={() => null}
              itemRender={(_, file, __, { remove }) => (
                <CustomItemRender file={file} onRemove={remove} />
              )}
            />
          </Form.Item>

          <Button type="primary" htmlType="submit" loading={isPending}>
            {t('roles.confirm')}
          </Button>
        </Flex>
      </Card>
    </Form>
  )
}
